import { TableRecordForm } from "blocks/components/TableRecordForm";
import { Modal, ModalHeader } from "blocks/components/Modal";
import { Spinner } from "blocks/components/Spinner";
import { APIRecord } from "server/services/tables/types/records";
import { APITable } from "server/services/tables/types/tables";
import { trpc } from "utils/trpc";
import { Text } from "blocks/components/Text";
import toast from "react-hot-toast";
import { useCallback } from "react";
import { useTrackingContext } from "observability";
import { usePageContext } from "lib/context/page-context";

export function ModalEditTableRecord(props: {
  kanbanId?: string;
  tableId: APITable["id"];
  recordId: APIRecord["id"] | undefined;
  newRecord: Partial<APIRecord> | undefined;
  canDelete: boolean;
  onClosed: () => void;
}) {
  const isNew = !props.recordId;

  const { emitConsumerInteractionEvent } = useTrackingContext();
  const { pageId, projectId, isEmbedded } = usePageContext();

  const { data: fieldsResponse } = trpc.blockKanban.getFields.useQuery(
    {
      tableId: props.tableId,
      kanbanId: props.kanbanId as string,
    },
    { suspense: false, enabled: !!props.kanbanId }
  );

  const {
    data: recordResponse,
    isFetchedAfterMount: recordsFetchedAfterMount,
  } = trpc.blockKanban.getRecord.useQuery(
    {
      tableId: props.tableId,
      kanbanId: props.kanbanId as string,
      recordId: props.recordId as string,
    },
    {
      enabled: !isNew && !!props.kanbanId,
      suspense: false,
      refetchOnMount: "always",
      keepPreviousData: false,
    }
  );

  const utils = trpc.useContext();

  const createRecordMutation = trpc.blockKanban.createRecord.useMutation({
    onSuccess() {
      emitConsumerInteractionEvent({
        interaction_goal: "Create kanban card",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "kanban",
        embedded_flag: isEmbedded,
      });
      toast.success("Card created successfully.");
      utils.tableRecords.query.refetch();
      utils.blockKanban.get.refetch();
      props.onClosed();
    },
  });

  const updateRecordMutation = trpc.blockKanban.updateRecord.useMutation({
    onSuccess() {
      emitConsumerInteractionEvent({
        interaction_goal: "Edit kanban card",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "kanban",
        embedded_flag: isEmbedded,
      });
      toast.success("Card updated successfully.");
      utils.tableRecords.query.refetch();
      utils.blockKanban.get.refetch();
      props.onClosed();
    },
  });

  const deleteRecordMutation = trpc.blockKanban.deleteRecord.useMutation({
    onSuccess() {
      emitConsumerInteractionEvent({
        interaction_goal: "Delete kanban card",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "kanban",
        embedded_flag: isEmbedded,
      });
      toast.success("Card successfully deleted");
      utils.tableRecords.query.refetch();
      utils.blockKanban.get.refetch();
      props.onClosed();
    },
  });

  const isSubmitting =
    createRecordMutation.isLoading || updateRecordMutation.isLoading;

  const handleSubmit = useCallback(
    (data: APIRecord["data"]) => {
      if (props.kanbanId === undefined || props.tableId === undefined) return;

      if (isNew) {
        createRecordMutation.mutate({
          kanbanId: props.kanbanId,
          tableId: props.tableId,
          body: { new_records: [{ data: data }] },
        });
      } else {
        if (!props.recordId) return;

        updateRecordMutation.mutate({
          kanbanId: props.kanbanId,
          tableId: props.tableId,
          body: { updated_records: [{ id: props.recordId, data: data }] },
        });
      }
    },
    [
      props.kanbanId,
      props.tableId,
      props.recordId,
      isNew,
      createRecordMutation,
      updateRecordMutation,
    ]
  );

  const handleDelete =
    props.canDelete && !isNew && props.recordId
      ? async () => {
          if (props.recordId && props.kanbanId) {
            await deleteRecordMutation.mutateAsync({
              kanbanId: props.kanbanId,
              tableId: props.tableId,
              body: { record_ids: [props.recordId] },
            });
          }

          props.onClosed();
        }
      : undefined;

  const renderBody = () => {
    if (
      (!isNew && (!recordsFetchedAfterMount || !recordResponse)) ||
      !fieldsResponse
    )
      return <Spinner height={200} />;

    const record = recordResponse?.data ?? props.newRecord;

    return (
      <TableRecordForm
        fields={fieldsResponse.data}
        record={record}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onCancel={props.onClosed}
        isSubmitting={isSubmitting}
        isDeleting={deleteRecordMutation.isLoading}
        tableId={props.tableId}
      />
    );
  };

  return (
    <Modal onClosed={props.onClosed}>
      <ModalHeader>
        <Text as="div" type="modalTitle">
          Card details
        </Text>
      </ModalHeader>
      {renderBody()}
    </Modal>
  );
}
